import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Crown, Compass, Loader2, Ghost, Wifi } from "lucide-react";

/**
 * Hunter-facing presence card. Rendered on the homepage.
 * Shows one of three states:
 *   • on_site — gold card with location + attire + Claim Founder Badge button
 *   • stealth — ember card with a riddle
 *   • off_site — subtle monitoring notice
 */
export default function FounderCard() {
  const { user } = useAuth();
  const [presence, setPresence] = useState(null);
  const [claiming, setClaiming] = useState(false);
  const [claimed, setClaimed] = useState(false);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get("/founder/presence");
        if (!alive) return;
        setPresence(data);
      } catch {
        /* ignore */
      }
    };
    load();
    const t = setInterval(load, 30000); // 30s refresh while homepage is open
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, []);

  // Hide whether this user already has the badge so we can gray out the CTA.
  useEffect(() => {
    if (user?.badges?.includes("Found the Founder")) setClaimed(true);
  }, [user]);

  // Render whenever the founder is actively on-site or in stealth mode.
  // Off-site = no card (we don't pollute the homepage with a "remotely"
  // message when nothing's happening). Card no longer requires a live drop —
  // founder meet-ups can happen any time.
  if (!presence) return null;
  if (presence.mode === "off_site") return null;

  const claim = () => {
    if (!user) {
      toast.info("Create a hunter account first — takes 15 seconds.");
      return;
    }
    if (claimed) return;
    if (!navigator.geolocation) {
      toast.error("Geolocation not supported.");
      return;
    }
    setClaiming(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { data } = await api.post("/founder/claim-badge", {
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
          });
          setClaimed(true);
          toast.success(`🏆 ${data.badge} — welcome to the club.`);
          if (data.streak_badges_unlocked?.length) {
            for (const b of data.streak_badges_unlocked) {
              setTimeout(() => toast.success(`🌟 Streak unlocked: ${b}`), 600);
            }
          }
          // Prompt the selfie (Improvement E). Most folks will decline, and
          // that's fine — the 5% who tap turn into TikTok marketing.
          setTimeout(() => {
            if (window.confirm("Want to grab a selfie with the founder?\n\nThey'll get a ping to come say hi.")) {
              api.post("/notifications/selfie-request", {}).catch(() => {});
            }
          }, 1500);
        } catch (e) {
          toast.error(formatApiError(e.response?.data?.detail) || "Claim failed");
        } finally {
          setClaiming(false);
        }
      },
      () => {
        setClaiming(false);
        toast.error("Enable location to claim the Founder badge.");
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
    );
  };

  // -------------- STEALTH --------------
  if (presence.mode === "stealth") {
    return (
      <div
        className="loot-card p-5 relative overflow-hidden"
        style={{
          borderColor: "#E53935",
          background: "linear-gradient(135deg, rgba(229,57,53,0.12), rgba(15,15,15,1) 60%)",
        }}
        data-testid="founder-card-stealth"
      >
        <div className="flex items-center gap-2 mb-2">
          <Ghost size={16} style={{ color: "#E53935" }} />
          <span className="overline" style={{ color: "#E53935" }}>Stealth mode</span>
          {presence.city && (
            <span className="font-mono text-[10px] text-[#F5F2E8]/50 ml-auto">
              · {presence.city}
            </span>
          )}
        </div>
        <div className="font-display uppercase text-2xl md:text-3xl tracking-tight">
          The Loot Master is in the zone…
        </div>
        {presence.stealth_riddle && (
          <p
            className="mt-3 font-mono text-sm text-[#F5F2E8]/85 italic border-l-2 pl-3"
            style={{ borderColor: "#E53935" }}
          >
            "{presence.stealth_riddle}"
          </p>
        )}
        <div className="mt-3 font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/55">
          First to find him gets a hint.
        </div>
      </div>
    );
  }

  // -------------- ON-SITE --------------
  return (
    <div
      className="loot-card p-5 relative overflow-hidden"
      style={{
        borderColor: "#D7A62A",
        background: "linear-gradient(135deg, rgba(215,166,42,0.14), rgba(15,15,15,1) 55%)",
      }}
      data-testid="founder-card-onsite"
    >
      <div className="flex items-center gap-2 mb-2">
        <Wifi size={16} style={{ color: "#D7A62A" }} className="animate-pulse" />
        <span className="overline" style={{ color: "#D7A62A" }}>Founder on-site</span>
        {presence.city && (
          <span className="font-mono text-[10px] text-[#F5F2E8]/60 ml-auto">
            · {presence.city}
          </span>
        )}
      </div>
      <div className="font-display uppercase text-2xl md:text-3xl tracking-tight">
        The founder is here.
      </div>
      <div className="mt-3 space-y-1 text-sm">
        {presence.location_note && (
          <div className="text-[#F5F2E8]/85">
            <span className="font-mono text-[10px] uppercase tracking-widest text-[#D7A62A] mr-2">Spot</span>
            {presence.location_note}
          </div>
        )}
        {presence.attire_note && (
          <div className="text-[#F5F2E8]/85">
            <span className="font-mono text-[10px] uppercase tracking-widest text-[#D7A62A] mr-2">Wearing</span>
            {presence.attire_note}
          </div>
        )}
      </div>
      <button
        onClick={claim}
        disabled={claiming || claimed}
        className="btn-gold mt-4 inline-flex items-center gap-2"
        data-testid="founder-claim-btn"
      >
        {claiming ? (
          <Loader2 size={14} className="animate-spin" />
        ) : claimed ? (
          <Crown size={14} />
        ) : (
          <Compass size={14} />
        )}
        {claiming
          ? "Checking your location…"
          : claimed
          ? "You already met the founder · 👑"
          : "Claim my Founder Badge"}
      </button>
      <div className="mt-2 font-mono text-[10px] text-[#F5F2E8]/50 uppercase tracking-widest">
        One-time · rare badge · must be within ~50 ft
      </div>
    </div>
  );
}
